import { logClientError } from '@utils/server-log';
import { fetchWithBackendLog } from '@utils/http-log';
import { inject } from '@angular/core';
import { SocialAuthProvider, User } from '@models/user';
import { SharedService } from '@services/shared/shared.service';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { environment } from 'src/environments/environment';
import { CognitoBase } from './cognito-bases';

export class CognitoProvider extends CognitoBase {
  shared = inject(SharedService);
  constructor() {
    super();
  }

  // Função para gerar um code_verifier aleatório
  generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  // Função para gerar um code_challenge a partir do code_verifier
  async generateCodeChallenge(codeVerifier: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  async loginWithProvider(provider: SocialAuthProvider) {
    // Gerar o Code Verifier e Code Challenge para PKCE
    const codeVerifier = this.generateCodeVerifier();
    localStorage.setItem('code_verifier', codeVerifier); // Salvar para troca depois
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    const authUrl = `${environment.cognito.domain}/oauth2/authorize?identity_provider=${provider}&response_type=${environment.cognito.responseType}&client_id=${environment.cognito.userClientId}&redirect_uri=${window.location.origin}&scope=${encodeURIComponent(
      environment.cognito.scope
    )}&code_challenge_method=S256&code_challenge=${codeChallenge}`;

    window.location.href = authUrl;
  }

  async exchangeCodeForToken(code: string): Promise<void> {
    const codeVerifier = localStorage.getItem('code_verifier');
    if (!codeVerifier) {
      logClientError('Code Verifier não encontrado!');
      return;
    }

    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', environment.cognito.userClientId);
    params.append('code', code);
    params.append('redirect_uri', window.location.origin);
    params.append('code_verifier', codeVerifier);
    params.append('scope', environment.cognito.scope);

    try {
      const response = await fetchWithBackendLog(`${environment.cognito.domain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const data = await response.json();

      if (!data.access_token) throw new Error('Token não recebido');

      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('idToken', data.id_token);
      localStorage.setItem('refreshToken', data.refresh_token);

      await this.fetchUserAttributes(data.access_token);
    } catch (error) {
      logClientError('Erro ao obter tokens:', error);
      return;
    }
  }

  getUsernameFromToken(idToken: string): string {
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));

      return payload['cognito:username'] || payload.email || payload.sub || '';
    } catch (error) {
      logClientError('Erro ao extrair username do idToken:', error);
      return '';
    }
  }

  async fetchUserAttributes(accessToken: string): Promise<User | null> {
    try {
      const response = await fetchWithBackendLog(`${environment.cognito.domain}/oauth2/userInfo`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const userData = await response.json();

      this.shared.user$.next(userData);

      if (!userData.sub) {
        throw new Error('Usuário inválido retornado.');
      }

      const cognitoUser = new CognitoUser({
        Username: this.getUsernameFromToken(localStorage.getItem('idToken')!),
        Pool: this.userPool,
      });

      const session = new CognitoUserSession({
        IdToken: new CognitoIdToken({
          IdToken: localStorage.getItem('idToken')!,
        }),
        AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
        RefreshToken: new CognitoRefreshToken({
          RefreshToken: localStorage.getItem('refreshToken')!,
        }),
      });

      cognitoUser.setSignInUserSession(session);

      return userData;
    } catch (error) {
      logClientError('Erro ao buscar dados do usuário:', error);
      return null;
    }
  }

  /**
   * Faz os dois primeiros passos do fluxo passwordless:
   * 1) InitiateAuth (USER_AUTH) para obter session1
   * 2) RespondToAuthChallenge (SELECT_CHALLENGE) para disparar o e-mail OTP
   *
   * Retorna a session2, que será usada posteriormente para validar o OTP.
   */
  public async requestEmailOTP(email: string): Promise<string> {
    try {
      // Passo 1: Inicia o fluxo sem senha e obtém a primeira session
      const session1 = await this.initiateEmailOTPAuth(email);

      // Passo 2: Envia a solicitação SELECT_CHALLENGE para disparar o e-mail com o OTP
      const session2 = await this.sendEmailOTP(email, session1);

      // Retorna a session do segundo passo, que será usada na tela de verificação
      return session2;
    } catch (error) {
      logClientError('Erro em requestEmailOTP:', error);
      throw error;
    }
  }

  /**
   * 3) Valida o código OTP recebido por e-mail. Caso sucesso, retorna o "User" (ou tokens)
   *
   * @param email string - email do usuário
   * @param otpCode string - código que veio no e-mail
   * @param session string - Session retornada no passo "sendEmailOTP"
   * @returns Promise<User|null> - Retorna o objeto de usuário ou null se falhar
   */
  public async validateEmailOTP(
    email: string,
    otpCode: string,
    session: string
  ): Promise<User | null> {
    try {
      const response = await fetchWithBackendLog(
        `https://cognito-idp.${environment.cognito.region}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
          },
          body: JSON.stringify({
            ChallengeName: 'EMAIL_OTP',
            ClientId: environment.cognito.userClientId,
            Session: session,
            ChallengeResponses: {
              USERNAME: email,
              EMAIL_OTP_CODE: otpCode,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.AuthenticationResult) {
        throw new Error(`Erro ao validar código OTP: ${data.__type || 'Desconhecido'}`);
      }

      const { AccessToken, IdToken, RefreshToken } = data.AuthenticationResult;

      localStorage.setItem('accessToken', AccessToken);
      localStorage.setItem('idToken', IdToken);
      localStorage.setItem('refreshToken', RefreshToken);

      const cognitoUserSession = new CognitoUserSession({
        IdToken: new CognitoIdToken({ IdToken: IdToken }),
        AccessToken: new CognitoAccessToken({ AccessToken: AccessToken }),
        RefreshToken: new CognitoRefreshToken({ RefreshToken: RefreshToken }),
      });

      const cognitoUser = new CognitoUser({
        Username: this.getUsernameFromToken(IdToken),
        Pool: this.userPool,
      });

      cognitoUser.setSignInUserSession(cognitoUserSession);

      const user = await this.fetchUserAttributes(AccessToken);
      this.shared.user$.next(user);

      return user;
    } catch (error: any) {
      logClientError('Erro em validateEmailOTP:', error);
      throw error;
    }
  }

  /**
   * 1) Inicia o fluxo "Passwordless" e retorna a primeira Session
   *
   * @param email string - email do usuário (já cadastrado manualmente no Cognito)
   * @returns Promise<string> - Session retornada na chamada do Cognito
   */
  private async initiateEmailOTPAuth(email: string): Promise<string> {
    try {
      const response = await fetchWithBackendLog(
        `https://cognito-idp.${environment.cognito.region}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
          },
          body: JSON.stringify({
            AuthFlow: 'USER_AUTH', // Importante: deve estar habilitado no User Pool com fluxo customizado
            ClientId: environment.cognito.userClientId,
            AuthParameters: {
              USERNAME: email,
            },
          }),
        }
      );

      const data = await response.json();
      if (!response.ok || !data.Session) {
        throw new Error(`Erro ao iniciar sessão Passwordless: ${data.__type || 'Desconhecido'}`);
      }

      return data.Session;
    } catch (error: any) {
      logClientError('Erro em initiateEmailOTPAuth:', error);
      throw error;
    }
  }

  /**
   * 2) Dispara o e-mail com o OTP. Retorna a próxima Session a ser usada
   *
   * @param email string - email do usuário
   * @param session string - Session retornada no passo "initiateEmailOTPAuth"
   * @returns Promise<string> - Nova Session retornada pelo Cognito
   */
  private async sendEmailOTP(email: string, session: string): Promise<string> {
    try {
      const response = await fetchWithBackendLog(
        `https://cognito-idp.${environment.cognito.region}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
          },
          body: JSON.stringify({
            ChallengeName: 'SELECT_CHALLENGE',
            ClientId: environment.cognito.userClientId,
            Session: session,
            ChallengeResponses: {
              USERNAME: email,
              ANSWER: 'EMAIL_OTP', // "Resposta" que faz o Cognito disparar o e-mail
            },
          }),
        }
      );

      const data = await response.json();
      if (!response.ok || !data.Session) {
        throw new Error(`Erro ao solicitar OTP via e-mail: ${data.__type || 'Desconhecido'}`);
      }

      return data.Session;
    } catch (error: any) {
      logClientError('Erro em sendEmailOTP:', error);
      throw error;
    }
  }
}
